import React from 'react';
import {
    Box,
    Container,
    Typography,
    useTheme,
    alpha
} from '@mui/material';
import SectionWrapper from '../components/SectionWrapper';
import GenesisV2IntelSeedA from '../components/GenesisV2_Intel_SeedA';

/**
 * PURPOSE PAGE - ALIGNED AESTHETIC V2.0 (Refactored)
 * Implements "One Section = One Page" Layout
 * Uses SectionWrapper for strict snap-scrolling and internal overflow handling.
 */
const PurposePage = () => {
    const theme = useTheme();

    return (
        <Box sx={{ width: '100%', height: '100%', bgcolor: '#050505' }}>

            {/* -----------------------------------------------------------------
               1. HERO SECTION: "THE GUILLOTINE"
               ----------------------------------------------------------------- */}
            <SectionWrapper
                id="purpose-hero"
                sx={{
                    bgcolor: '#050505',
                    color: 'common.white',
                    justifyContent: 'center', // Center vertically
                    alignItems: 'center'
                }}
            >
                {/* AMBIENT LIGHTING */}
                <Box sx={{
                    position: 'absolute', top: '-20%', right: '-10%',
                    width: '60vw', height: '60vw',
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 70%)`,
                    filter: 'blur(100px)',
                    zIndex: 0,
                    pointerEvents: 'none'
                }} />

                {/* THE GUILLOTINE */}
                <Box sx={{
                    position: 'relative',
                    zIndex: 2,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1 // Take available space
                }}>
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: '20vw', // Slightly increased for impact
                            fontWeight: 900,
                            lineHeight: 0.75,
                            letterSpacing: '-0.08em',
                            color: 'common.white',
                            whiteSpace: 'nowrap',
                            userSelect: 'none',
                            fontFamily: "'Inter', sans-serif",
                            textAlign: 'center', // Ensure alignment
                        }}
                    >
                        PROPÓSITO
                    </Typography>
                </Box>

                {/* SUBTEXT */}
                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 3, textAlign: 'center', pb: 20 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            color: 'primary.main',
                            letterSpacing: '0.5em',
                            fontWeight: 600,
                            display: 'block',
                            mb: 2,
                            fontSize: { xs: '0.7rem', md: '0.9rem' },
                        }}
                    >
                        INTELIGÊNCIA DIGITAL
                    </Typography>
                    <Typography
                        sx={{
                            color: 'grey.500',
                            fontWeight: 300,
                            maxWidth: '600px', // Slightly wider for this text
                            mx: 'auto',
                            fontSize: { xs: '1rem', md: '1.1rem' },
                            px: 2
                        }}
                    >
                        Solução sem propósito é uma promessa vazia. <br />
                        <span style={{ color: '#fff', display: 'block', marginTop: '16px' }}>
                            Transformamos tecnologias em valor real.
                        </span>
                    </Typography>
                </Container>

            </SectionWrapper>

            {/* -----------------------------------------------------------------
               3. GENESIS V2: INTELLIGENCE SECTION (SEED A - STRUCTURAL)
               ----------------------------------------------------------------- */}
            <SectionWrapper
                id="purpose-intelligence"
                sx={{
                    p: 0,
                    bgcolor: '#f4f4f4', // Match Seed A background
                    overflow: 'hidden' // Let component handle internal scroll if needed
                }}
            >
                <GenesisV2IntelSeedA />
            </SectionWrapper>

            {/* -----------------------------------------------------------------
               4. MANIFESTO SECTION (BLUEPRINT V3 (BIO OVERLAY))
               ----------------------------------------------------------------- */}
            <SectionWrapper
                id="purpose-manifesto"
                sx={{ bgcolor: '#ffffff', justifyContent: 'center' }}
            >
                {/* GENESIS V2: SEED A - BLUEPRINT V10 (THE REDEMPTOR) */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 640px 130px' }, // Content | Image (Wider Again) | Nav Lane
                    height: '100%',
                    minHeight: 0, // allow children to shrink within fixed SectionWrapper height
                    width: '100%',
                    boxSizing: 'border-box',
                    borderTop: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                }}>

                    {/* COL 1: MAIN CONTENT */}
                    <Box sx={{
                        p: { xs: 4, md: 8 },
                        pl: { md: 10 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        position: 'relative',
                        borderRight: { md: '1px solid #e0e0e0' },
                        minHeight: 0
                    }}>
                        {/* Blueprint Marker */}
                        <Typography sx={{
                            position: 'absolute',
                            top: 24,
                            left: 24,
                            fontFamily: 'monospace',
                            fontSize: '0.9rem', // Increased Size
                            color: '#CC0000',
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase'
                        }}>
                            [ MISSÃO ]
                        </Typography>

                        <Typography variant="h3" sx={{
                            fontFamily: "'Playfair Display', serif",
                            fontStyle: 'italic',
                            fontWeight: 600,
                            fontSize: { xs: '2.5rem', md: '4.2rem' }, // Larger
                            lineHeight: 1.05,
                            color: '#050505',
                            mb: 6,
                            mt: 4,
                            letterSpacing: '-0.03em'
                        }}>
                            "O verdadeiro salto <br />não é tecnológico. <br />É estratégico."
                        </Typography>

                        <Typography sx={{
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            color: '#444',
                            lineHeight: 1.7,
                            maxWidth: '600px',
                            fontWeight: 300,
                            fontFamily: '"Inter", sans-serif',
                            // Keep this block responsive in shorter viewports.
                            overflowY: 'auto',
                            pr: 1,
                            '&::-webkit-scrollbar': { width: '3px' },
                            '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.05)' },
                            '&::-webkit-scrollbar-thumb': { background: '#CC0000', borderRadius: '4px' }
                        }}>
                            Inteligência Digital não é sobre a tecnologia em si, mas sobre a capacidade de transformar contextos e qualificar decisões por meio do desenvolvimento de soluções tecnológicas.
                            <br /><br />
                            É para esse propósito que a R5 existe; para transformar conhecimento em soluções digitais que geram transformação e resultados de verdade. Tecnologias capazes de mudar a realidade, a vida das pessoas e as organizações.
                        </Typography>

                    </Box>

                    {/* COL 2: IMAGE + REFINED BIO */}
                    <Box sx={{
                        position: 'relative',
                        height: '100%',
                        minHeight: 0,
                        borderRight: { md: '1px solid #e0e0e0' },
                        overflow: 'hidden', // Contain everything
                        display: 'flex',
                        flexDirection: 'column',
                        // INTERACTION: Colorize image on HOVER over key area
                        '&:hover img': {
                            filter: 'grayscale(0%)',
                            transform: 'scale(1.02)' // Subtle zoom on hover for feedback
                        }
                    }}>
                        {/* IMAGE AREA (always reserves space; prevents the bio from "eating" the photo) */}
                        <Box sx={{
                            flex: '1 1 auto',
                            // Avoid dvh here: the available height is `--page-h` (viewport minus header).
                            // Using dvh can exceed the section container and cause bottom overflow.
                            minHeight: 0,
                            position: 'relative',
                            overflow: 'hidden',
                            bgcolor: '#111', // safe letterbox when image switches to contain on short screens
                        }}>
                            <Box
                                component="img"
                                src="/images/ceo-r5.jpg"
                                alt="Fábio de Souza"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    // Framing: keep head/shoulders, crop a bit of lower torso.
                                    objectPosition: 'center 22%',
                                    filter: 'grayscale(100%)',
                                    transition: 'filter 0.5s ease, transform 0.7s ease',
                                    // On short viewports, keep a tighter crop (still within reserved image area).
                                    '@media (max-height: 760px)': {
                                        objectFit: 'cover',
                                        objectPosition: 'center 18%',
                                    }
                                }}
                            />
                        </Box>

                        {/* BIO BOX - RESPONSIVE (stacked, scrolls internally; never overflows the viewport) */}
                        <Box sx={{
                            flex: '0 0 auto',
                            // Use % of container height (not dvh) to stay within SectionWrapper's fixed height.
                            // Give the bio a bit more room (at the expense of a small image crop),
                            // while still staying within the fixed-height section.
                            maxHeight: 'clamp(250px, 56%, 620px)',
                            minHeight: { xs: 160, md: 200 },
                            bgcolor: 'rgba(255, 255, 255, 0.85)', // Less opaque, more glass
                            backdropFilter: 'blur(20px)',
                            borderTop: '1px solid rgba(255,255,255,0.8)',
                            p: { xs: 3, md: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            // On very short viewports, reduce padding to keep everything within the section.
                            '@media (max-height: 760px)': {
                                p: 2.5,
                                maxHeight: 'clamp(250px, 62%, 660px)',
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ width: '30px', height: '2px', bgcolor: '#CC0000', mr: 2 }} />
                                <Typography variant="caption" sx={{
                                    color: '#CC0000',
                                    fontWeight: 900,
                                    letterSpacing: '0.15em',
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase'
                                }}>
                                    Chief Executive Officer - CEO
                                </Typography>
                            </Box>

                            <Typography variant="h4" sx={{
                                fontWeight: 800,
                                color: '#000',
                                mb: 2,
                                fontSize: '1.8rem',
                                letterSpacing: '-0.02em',
                                fontFamily: '"Inter", sans-serif' // Brutalist Sans
                            }}>
                                Fábio de Souza, Ph.D.
                            </Typography>

                            <Box sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                pr: 2,
                                // Styled Scrollbar
                                '&::-webkit-scrollbar': { width: '3px' },
                                '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.05)' },
                                '&::-webkit-scrollbar-thumb': { background: '#CC0000', borderRadius: '4px' }
                            }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: '"Inter", sans-serif',
                                        color: '#333',
                                        lineHeight: 1.62,
                                        // Responsive typography: adapts across wide vs short screens.
                                        fontSize: 'clamp(0.86rem, calc(0.6vw + 0.45vh), 1.02rem)',
                                        fontWeight: 300,
                                        textAlign: 'justify',
                                    }}
                                >
                                    Doutor e Mestre pela Universidade de Brasília (UnB), especialista em Gestão de Tecnologia da Informação, com mais de 20 anos de experiência em gestão e desenvolvimento de tecnologias; atuou como Secretário-Executivo de Ciência, Tecnologia e Inovação do Distrito Federal, Coordenador-Geral de Governança e Gestão de TIC do Ministério da Educação (MEC) e Diretor de Tecnologia do Detran-DF, também exerceu cargos de direção e assessoramento na DATAPREV, Ministério da Fazenda, Advocacia-Geral da União (AGU) e Procuradoria-Geral Federal (PGF), Procuradoria Regional da União (PRU-1), liderando projetos estruturantes de modernização e transformação digital; também foi professor e pesquisador na UnB e ministra cursos em escolas de governo como ENAP, ESAF e EAGU, sendo reconhecido por sua contribuição em projetos de modernização e transformação digital no governo.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* COL 3: TECHNICAL BAR (SAFE LANE) */}
                    <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        pl: 4,
                        bgcolor: '#fafafa',
                        borderRight: { md: '1px solid #e0e0e0' }
                    }}>
                        <Typography variant="caption" sx={{
                            writingMode: 'vertical-rl',
                            textOrientation: 'mixed',
                            fontFamily: 'monospace',
                            color: '#bbbbbb',
                            letterSpacing: '0.15em',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                        }}>
                            SYS.VIS.01 // Leadership
                        </Typography>
                    </Box>
                </Box>
            </SectionWrapper>



        </Box>
    );
};

export default PurposePage;
